import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import Button from "../components/mui/material/Button";
import { subscriptionStateIds } from "../constants"
import { RootState } from "../store";

const tKey = "contexts.SubscriptionContext";

interface SubscriptionContextType {
  dialogOpen: boolean;
  toggleSubscriptionDialogOpen: (featureId: number) => void;
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const authUserId = useSelector((state: RootState) => state.authentication.userId);
  const authUserSubscription = useSelector((state: RootState) => state.entities.subscriptions.byUserId[authUserId]);
  const authUserSubscribed =
    authUserSubscription &&
    authUserSubscription.state.id === subscriptionStateIds.AUTHORIZED;
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState<boolean>(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState<any>(null);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  function toggleSubscriptionDialogOpen(featureId: number) {
    setSubscriptionDialogOpen(!subscriptionDialogOpen);
    handleSelect(featureId);
  }

  const handleSelect = (featureId: number) => {
    setSelectedFeatureId((prev: number | null) => (prev === featureId ? null : featureId));
  };

  const renderValue = (value: boolean | string | number): JSX.Element => {
    if (typeof value === "boolean") {
      return value ? (
        <CheckIcon color="success" />
      ) : (
        <CloseOutlinedIcon color="error" />
      );
    }

    return <Typography>{value}</Typography>;
  };

  // TODO fix this 
  useEffect(() => {
    if (subscriptionDialogOpen && selectedFeatureId && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      bottomRef.current.focus();
    }
  }, [subscriptionDialogOpen]);

  const features = [
    {
      id: 1,
      name: t(`${tKey}.socialCommunity_name`),
      description: t(`${tKey}.socialCommunity_description`),
      standard: true,
      plus: true,
    },
    {
      id: 2,
      name: t(`${tKey}.collaborativeInvesting_name`),
      description: t(`${tKey}.collaborativeInvesting_description`),
      standard: true,
      plus: true,
    },
    {
      id: 3,
      name: t(`${tKey}.roboAdvisor_name`),
      description: t(`${tKey}.roboAdvisor_description`),
      standard: true,
      plus: true,
    },
    {
      id: 4,
      name: t(`${tKey}.goalBasedInvesting_name`),
      description: t(`${tKey}.goalBasedInvesting_description`),
      standard: true,
      plus: true,
    },
    {
      id: 5,
      name: t(`${tKey}.portfolioManagement_name`),
      description: t(`${tKey}.portfolioManagement_description`),
      standard: true,
      plus: true,
    },
    {
      id: 6,
      name: t(`${tKey}.multiBrokerInvesting_name`),
      description: t(`${tKey}.multiBrokerInvesting_description`),
      standard: true,
      plus: true,
    },
    {
      id: 7,
      name: t(`${tKey}.interactive3DVisuals_name`),
      description: t(`${tKey}.interactive3DVisuals_description`),
      standard: true,
      plus: true,
    },
    {
      id: 8,
      name: t(`${tKey}.aiChat_name`),
      description: t(`${tKey}.aiChat_description`),
      standard: t(`${tKey}.aiChat_standard`),
      plus: t(`${tKey}.aiChat_plus`),
    },
    {
      id: 9,
      name: t(`${tKey}.multiDimensionInvestmentPerformance_name`),
      description: t(`${tKey}.multiDimensionInvestmentPerformance_description`),
      standard: false,
      plus: true,
    },
    {
      id: 10,
      name: t(`${tKey}.comparativePriceAnalysis_name`),
      description: t(`${tKey}.comparativePriceAnalysis_description`),
      standard: false,
      plus: true,
    },
    {
      id: 11,
      name: t(`${tKey}.aiStockPrediction_name`),
      description: t(`${tKey}.aiStockPrediction_description`),
      standard: false,
      plus: true,
    },
    {
      id: 12,
      name: t(`${tKey}.aiRecommendedPortfolio_name`),
      description: t(`${tKey}.aiRecommendedPortfolio_description`),
      standard: false,
      plus: true,
    },
    {
      id: 13,
      name: t(`${tKey}.exclusiveReports_name`),
      description: t(`${tKey}.exclusiveReports_description`),
      standard: false,
      plus: true,
    },
  ];
  const selected = features.find((f) => f.id === selectedFeatureId);

  return (
    <SubscriptionContext.Provider value={{ toggleSubscriptionDialogOpen }}>
      {children}
      <Dialog
        fullWidth
        open={subscriptionDialogOpen}
        onClose={toggleSubscriptionDialogOpen}
        scroll="body"
        maxWidth="md"
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          },
        }}
        sx={{
          backdropFilter: "blur(5px) sepia(5%)",
        }}
      >
        <DialogContent sx={{ position: "relative", p: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="center">Standard (Free)</TableCell>
                <TableCell align="center">Plus</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map((feature) => (
                <TableRow
                  key={feature.id}
                  hover
                  onClick={() => handleSelect(feature.id)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedFeatureId === feature.id
                        ? "action.selected"
                        : "inherit",
                  }}
                >
                  <TableCell>{feature.name}</TableCell>
                  <TableCell align="center">
                    {renderValue(feature.standard)}
                  </TableCell>
                  <TableCell align="center">
                    {renderValue(feature.plus)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {selected && (
            <Paper elevation={0} sx={{ mt: 3, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selected.name}
              </Typography>
              <Typography variant="body2">{selected.description}</Typography>
            </Paper>
          )}

          {authUserSubscribed ?
            <Box mt={4} display="flex" justifyContent="center">
              <Typography variant="body2" className="text-muted">
                {t(`${tKey}.unsubscribePlusInstructions`)}
              </Typography>
            </Box> :
            <>
              <Box mt={4} display="flex" justifyContent="center">
                <Button
                  component={Link}
                  to="https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=2c93808496ce9c1b0196d5d696bb0360"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="primary"
                  size="large"
                  style={{ fontWeight: 600 }}
                >
                  {t(`${tKey}.upgradeToPlus`)}
                </Button>
              </Box>
              <Box mt={4} display="flex" justifyContent="center">
                <Typography variant="body2" className="text-muted">
                  {/* TODO add the following line after ARCA integration:
                  (en) "You will receive a receipt from us each time a payment is processed through MercadoPago."
                  (es-AR) "Vas a recibir un comprobante de nuestra parte cada vez que se procese un pago a través de MercadoPago."  */}
                  {t(`${tKey}.subscribePlusInstructions`)}
                </Typography>
              </Box>
            </>
          }

          {/* This ref will be scrolled into view */}
          <div ref={bottomRef}></div>
        </DialogContent>
      </Dialog>
    </SubscriptionContext.Provider>
  );
}

export const useSubscriptionContext = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context)
    throw new Error("useSubscriptionContext must be used within a SubscriptionProvider");
  return context;
};